// Tier 3 Progress dashboard math helpers (#9–#10). All pure functions
// over already-fetched data. See docs/progress-redesign.md.
import { DAY_MS } from "./freshness.js";
import type { Max } from "./schemas/max.js";
import type { TimedSessionResult } from "./progressTypes.js";
import { detectRegression, type RegressionResult } from "./progressTierOne.js";
import { e1rmPointsByTrack, topE1rmTrack } from "./e1rmPoints.js";

// =========================================================================
// Tier 3 #9 — Annual top-5 progression.
// =========================================================================
// Rank exercises by their current Max, take top N, then return the
// year-segmented progression points for each. The chart draws one line
// per ranked exercise across the calendar year selected by the caller.

export type AnnualProgressionLine = {
  slug: string;
  points: ReadonlyArray<{ atMs: number; kg: number }>;
};

export const annualTopLifts = (
  maxes: ReadonlyArray<Max>,
  year: number,
  topN: number,
): ReadonlyArray<AnnualProgressionLine> => {
  // Group maxes by exerciseId; sort each group chronologically.
  const grouped = new Map<string, Max[]>();
  for (const m of maxes) {
    const list = grouped.get(m.exerciseId) ?? [];
    list.push(m);
    grouped.set(m.exerciseId, list);
  }
  // Rank by latest valueKg per exercise.
  const ranked = Array.from(grouped.entries())
    .map(([slug, list]) => {
      const sorted = [...list].sort(
        (a, b) => Date.parse(a.setAt) - Date.parse(b.setAt),
      );
      const latest = sorted[sorted.length - 1];
      return {
        slug,
        latestKg: latest?.valueKg ?? 0,
        sorted,
      };
    })
    .filter((r) => r.latestKg > 0)
    .sort((a, b) => b.latestKg - a.latestKg)
    .slice(0, topN);
  // Filter each group to points within the requested year.
  const yearStart = Date.UTC(year, 0, 1);
  const yearEnd = Date.UTC(year + 1, 0, 1);
  return ranked.map(({ slug, sorted }) => ({
    slug,
    points: sorted
      .map((m) => ({ atMs: Date.parse(m.setAt), kg: m.valueKg }))
      .filter((p) => p.atMs >= yearStart && p.atMs < yearEnd),
  }));
};

// =========================================================================
// Tier 3 #10 — Auto-deload detection.
// =========================================================================
// Heuristic: last 4 weeks show rising total volume (≥ +10% vs the
// prior 4 weeks) AND e1RM on the top lift is flat-or-down (slope
// ≤ +0.1 kg/wk over the same 8-week window).

/** Minimum e1RM observations on the top lift before the deload heuristic
 *  will judge its trend. No external caller since PG-8 removed AlertsPanel; kept beside suggestDeload,
 *  which dies with the frozen legacy web (RW-14).
 *  question from outside — "was this signal even evaluable?" — and a second
 *  bare literal there could silently drift from this one. */
export const MIN_TOPLIFT_REGRESSION_POINTS = 3;

export type DeloadSuggestion = {
  shouldDeload: boolean;
  reason: string;
  volumeChangePct: number; // last 4w vs prior 4w
  topLiftTrack: string | null;
  topLiftRegression: RegressionResult | null;
};

export const suggestDeload = (
  sessions: TimedSessionResult[],
  nowMs: number,
): DeloadSuggestion => {
  const fourWeeksMs = 28 * DAY_MS;
  const recent: TimedSessionResult[] = [];
  const prior: TimedSessionResult[] = [];
  for (const s of sessions) {
    const age = nowMs - s.startedAtMs;
    if (age < 0) continue;
    if (age < fourWeeksMs) recent.push(s);
    else if (age < 2 * fourWeeksMs) prior.push(s);
  }
  const sumVol = (arr: TimedSessionResult[]) =>
    arr.reduce((acc, s) => acc + s.result.totalVolumeKg, 0);
  const recentVol = sumVol(recent);
  const priorVol = sumVol(prior);
  const volumeChangePct = priorVol === 0 ? 0 : (recentVol - priorVol) / priorVol;

  // Pick the athlete's top lift in the 8w window by frequency × max e1RM, then
  // read its series. Both come off ONE pass of the shared funnel
  // (e1rmPoints.ts), which is the point: scoring the lift and then describing
  // it used to be two hand-rolled walks, so "which observations count" was
  // stated twice about the same lift, twelve lines apart.
  const bySlug = e1rmPointsByTrack([...recent, ...prior]);
  const topLiftTrack = topE1rmTrack(bySlug);

  let topLiftRegression: RegressionResult | null = null;
  if (topLiftTrack) {
    topLiftRegression = detectRegression(
      bySlug.get(topLiftTrack)?.points ?? [],
      nowMs,
      8,
    );
  }

  const volumeRising = volumeChangePct >= 0.1;
  const e1rmFlatOrDown =
    topLiftRegression !== null &&
    topLiftRegression.pointCount >= MIN_TOPLIFT_REGRESSION_POINTS &&
    topLiftRegression.slopePerWeek <= 0.1;
  const shouldDeload = volumeRising && e1rmFlatOrDown;
  const reason = shouldDeload
    ? `Volume +${Math.round(volumeChangePct * 100)}% over last 4 wk vs prior 4 wk; top lift e1RM flat or down (${topLiftRegression!.flag}).`
    : "";
  return {
    shouldDeload,
    reason,
    volumeChangePct,
    topLiftTrack,
    topLiftRegression,
  };
};
